declare const __APP_VERSION__: string

interface CacheEnvelope<T> {
  v: string
  data: T
}

/**
 * localStorage for API payloads that pages render before any request goes out
 * (PWA offline support + navigation speed).
 *
 * Every entry is stamped with the build that wrote it. A payload written by an
 * older build can be missing fields the current code reads — which paints as
 * `undefined` on screen until the fetch lands — so anything from another build
 * is discarded instead of shown. The cost is one loading state per deploy.
 *
 * Device preferences (dismissed banners, feedback flags) do not belong here:
 * they describe the browser and must survive a deploy.
 */
export function readPayloadCache<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw) as CacheEnvelope<T> | null
    if (!parsed || parsed.v !== __APP_VERSION__) return null
    return parsed.data
  } catch {
    return null
  }
}

export function writePayloadCache<T>(key: string, data: T): void {
  try {
    const envelope: CacheEnvelope<T> = { v: __APP_VERSION__, data }
    localStorage.setItem(key, JSON.stringify(envelope))
  } catch {
    // localStorage full or unavailable — ignore
  }
}
